export const normalizeDegrees = (degrees) => ((degrees % 360) + 360) % 360;

export const signedAngleDifference = (from, to) =>
  ((to - from + 540) % 360) - 180;

export function buildRouteMetrics(path, metersPerPixel = 0.05) {
  const safePath = (Array.isArray(path) ? path : []).filter(
    (point) => Number.isFinite(point?.x) && Number.isFinite(point?.y)
  );
  const scale = Number(metersPerPixel) > 0 ? Number(metersPerPixel) : 0.05;
  const segments = [];
  let totalMeters = 0;

  for (let index = 0; index < safePath.length - 1; index += 1) {
    const start = safePath[index];
    const end = safePath[index + 1];
    const pixelLength = Math.hypot(end.x - start.x, end.y - start.y);
    if (pixelLength < 0.001) continue;
    const lengthMeters = pixelLength * scale;
    segments.push({
      index,
      start,
      end,
      startMeters: totalMeters,
      endMeters: totalMeters + lengthMeters,
      lengthMeters,
      bearing: normalizeDegrees(
        (Math.atan2(end.x - start.x, -(end.y - start.y)) * 180) / Math.PI
      ),
    });
    totalMeters += lengthMeters;
  }

  return { path: safePath, segments, totalMeters, metersPerPixel: scale };
}

export function positionAtDistance(metrics, requestedMeters) {
  const { segments = [], totalMeters = 0 } = metrics || {};
  if (!segments.length) return null;
  const distanceMeters = Math.max(0, Math.min(Number(requestedMeters) || 0, totalMeters));
  const segment =
    segments.find((item) => distanceMeters <= item.endMeters) ||
    segments[segments.length - 1];
  const localMeters = Math.max(0, distanceMeters - segment.startMeters);
  const progress = Math.min(1, localMeters / segment.lengthMeters);

  return {
    x: segment.start.x + (segment.end.x - segment.start.x) * progress,
    y: segment.start.y + (segment.end.y - segment.start.y) * progress,
    segmentIndex: segment.index,
    segmentProgress: progress,
    distanceMeters,
    remainingMeters: Math.max(0, totalMeters - distanceMeters),
    routeBearing: segment.bearing,
  };
}

export function distanceAlongRouteForPoint(metrics, point) {
  if (!point || !metrics?.segments?.length) return 0;
  let best = { distance: Infinity, routeMeters: 0 };

  metrics.segments.forEach((segment) => {
    const dx = segment.end.x - segment.start.x;
    const dy = segment.end.y - segment.start.y;
    const lenSq = dx * dx + dy * dy;
    const t = lenSq
      ? Math.max(
          0,
          Math.min(
            1,
            ((point.x - segment.start.x) * dx + (point.y - segment.start.y) * dy) /
              lenSq
          )
        )
      : 0;
    const projected = {
      x: segment.start.x + dx * t,
      y: segment.start.y + dy * t,
    };
    const distance = Math.hypot(point.x - projected.x, point.y - projected.y);
    if (distance < best.distance) {
      best = {
        distance,
        routeMeters: segment.startMeters + segment.lengthMeters * t,
      };
    }
  });

  return best.routeMeters;
}

export function stepIndexAtDistance(steps, metrics, distanceMeters, toleranceMeters = 0.8) {
  if (!Array.isArray(steps) || !steps.length) return 0;
  let activeIndex = 0;
  steps.forEach((step, index) => {
    const stepMeters = distanceAlongRouteForPoint(metrics, step?.point);
    if (distanceMeters + toleranceMeters >= stepMeters) activeIndex = index;
  });
  return Math.min(activeIndex, steps.length - 1);
}

